import { Box, Stack, Typography, Button } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { listArchivableSilobagService } from '@old/services/domain/base/silobag/base/get-archive-silobags';
import i18n from '../../libs/language';
import { formatDate, formatTons } from '@old/utils/helper';
import { useService } from '@old/hooks/use-service';
import { BaseTable } from '@old/ui/table';
import { configButtonStylesContained, typographyStyle } from '@old/styles/generic-styles';
import ChevronLeftIcon from '@old/assets/svg/core/chevron-left-blue.svg';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { ToastAction } from '@old/ui/toast/toast-action';
import { PATH } from '@old/data/core/paths';
import { useGlobalStore } from '@old/global-store/use-store';
import { useEffectOnUpdate } from '@old/hooks/core';
import { RestoreSbModal } from '@old/features/archivable-sb/restore-modal';

type SummaryArchivedSilobagList = Awaited<ReturnType<typeof listArchivableSilobagService>>['summary']['data'];
type ToastArchivable = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};
export function SilobagArchived() {
  const { organizationId } = useGlobalStore();
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState<SummaryArchivedSilobagList>([]);
  const [openModalRestore, setOpenModalRestore] = useState(false);
  const [toastArchivable, setToastArchivable] = useState<ToastArchivable>({
    open: false,
    message: '',
    severity: 'success',
  });
  const {
    summary: SbArchived,
    isLoading,
    trigger: getArchived,
  } = useService(listArchivableSilobagService, { fetchOnMount: true });

  useEffectOnUpdate(() => {
    getArchived();
  }, [organizationId]);

  const rows = SbArchived ? SbArchived.data : [];

  const columnsArchived: GridColDef<SummaryArchivedSilobagList['0']>[] = [
    {
      field: 'action',
      display: 'flex',
      headerName: 'SiloID',
      flex: 1,
      minWidth: 80,
      renderCell: (params) => {
        const siloId = params.row.id;
        return (
          <Stack>
            <Box style={{ fontSize: '0.85rem' }}>{siloId}</Box>
          </Stack>
        );
      },
    },
    {
      field: 'silobag_name',
      display: 'flex',
      headerName: 'Silo',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
    },
    {
      field: 'bagging_date',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.bagging_date'),
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        const date = params.value ? formatDate(params.value) : '―';
        return (
          <Stack>
            <Box sx={{ marginTop: '3px' }}>{date}</Box>
          </Stack>
        );
      },
    },
    {
      field: 'input_weight',
      display: 'flex',
      headerName: 'Peso',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        return (
          <span style={{ fontSize: '0.85rem' }}>
            {i18n.numberToDelimited(params.value, { delimiter: i18n.t('config.delimiter') })} Kg
          </span>
        );
      },
    },
    {
      field: 'organization_name',
      display: 'flex',
      headerName: i18n.t('app.organization'),
      flex: 1,
      minWidth: 115,
    },
    {
      field: 'countryside_name',
      display: 'flex',
      headerName: i18n.t('app.countryside'),
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <Stack style={{ fontSize: '0.85rem' }}>
            <Box>{params.value}</Box>
          </Stack>
        );
      },
    },
    {
      field: 'silobag_species_type_name',
      display: 'flex',
      headerName: i18n.t('app.silobag_data.species'),
      flex: 1.2,
      minWidth: 135,
      renderCell: (params) => {
        return (
          <Stack>
            <Stack direction="row" alignItems="center">
              <Box style={{ fontSize: '.85rem' }}>{params.value}</Box>
            </Stack>
          </Stack>
        );
      },
    },
  ];

  const onRowSelectionModelChange = (newRowSelection: SummaryArchivedSilobagList) => {
    setRowSelection(newRowSelection ?? []);
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const addedSilobags = rows.filter(
      (r) => newSelection.ids.has(r.id) && !rowSelection.find((ss) => ss.id === r.id)
    );
    const removedSilobags = rows.filter((r) => !newSelection.ids.has(r.id));
    const newDataSelected = rowSelection
      .concat(addedSilobags)
      .filter((ss) => !removedSilobags.find((r) => r.id === ss.id));
    onRowSelectionModelChange(newDataSelected);
  };
  const rowsSelectedIds = rowSelection.map((row) => row.id);

  return (
    <Box sx={{ maxWidth: '95%', margin: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<img src={ChevronLeftIcon} style={{ width: 10, height: 10 }} />}
            style={{ color: '#22A7F0', display: window.history.length <= 3 ? 'none' : '' }}
            onClick={() => navigate(PATH.SILOBAG)}
          />
          <Typography variant="h5" fontWeight={500} sx={{ marginY: '20px' }}>
            {i18n.t('app.archived_silobags.title')}
          </Typography>
        </Box>

        <Button
          sx={{ ...configButtonStylesContained, marginY: '20px' }}
          variant="contained"
          onClick={() => {
            setOpenModalRestore(true);
          }}
          disabled={rowsSelectedIds.length === 0}
        >
          {i18n.t('app.archived_silobags.restore')}
        </Button>
      </Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        style={{
          textAlign: 'left',
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginTop: '15px',
        }}
        width={'100%'}
        display={'flex'}
        position="relative"
        alignItems={{ xs: 'start', md: 'center' }}
      >
        <Stack direction={'row'} display={'flex'} alignItems={'center'}>
          <Box>
            <Typography
              component="span"
              style={{ borderRight: '1px solid #B1B3B4', padding: '8px 12px' }}
              sx={typographyStyle}
            >
              <span style={{ fontSize: '1.5rem' }}>
                {SbArchived?.totalFilteredSilobags} {i18n.t('app.archived_silobags.count_title')}{' '}
              </span>
            </Typography>
          </Box>
          <Typography component="span" sx={typographyStyle}>
            <span style={{ fontSize: '1.5rem' }}>
              {formatTons(SbArchived ? +SbArchived.filteredInputWeight : 0)} Toneladas{' '}
            </span>
          </Typography>
        </Stack>
      </Stack>
      <BaseTable
        rows={rows}
        columns={columnsArchived}
        loading={isLoading}
        initialPageSize={50}
        enableSelection
        checkboxSelection
        rowSelectionModel={{
          type: 'include',
          ids: new Set(rowSelection.map((row) => row.id)),
        }}
        onRowSelectionModelChange={handleSelectionChange}
      />
      {toastArchivable.open && (
        <ToastAction
          openToast={toastArchivable.open}
          message={toastArchivable.message}
          severity={toastArchivable.severity}
        />
      )}
      {openModalRestore && (
        <RestoreSbModal
          open={openModalRestore}
          setOpen={setOpenModalRestore}
          getArchived={getArchived}
          silobags={rowSelection}
          setToastArchivable={setToastArchivable}
          setRowSelection={setRowSelection}
        />
      )}
    </Box>
  );
}
